"use client";

import { useState } from "react";
import { Box, Button, Collapse, Link, Typography } from "@mui/material";
import SouthIcon from "@mui/icons-material/South";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { SponsorCampaign } from "../campaignTypes";
import { PORTAL_MAX_WIDTH, portalFont, portalShellSx } from "../partnerTheme";
import AmexBrandOverview from "./AmexBrandOverview";

const ACCENT = "#8EB8E5";
const LINE = "rgba(255,255,255,0.12)";
const MUTED = "rgba(244,245,247,0.62)";
const CARD_BG = "rgba(255,255,255,0.04)";
const RADIUS = "16px";

const IMG = {
  tickets: "https://storage.googleapis.com/cuo-website/ESB%20Tickets.jpg",
  food: "https://storage.googleapis.com/cuo-website/Food%20at%20ESB.jpg",
  socializing: "https://storage.googleapis.com/cuo-website/Socializing%20at%20ESB.jpg",
  crowd: "https://storage.googleapis.com/cuo-website/Crowd%20shot%20esb.jpg",
  tourGuide: "https://storage.googleapis.com/cuo-website/Tour%20Guide%20ESB.jpg",
  esbTopDeck: "https://storage.googleapis.com/cuo-website/ESB%20Top%20Deck.jpg",
  skyline: "https://storage.googleapis.com/cuo-website/People%20looking%20out%20at%20Skyline.jpg",
  observatoryCouple:
    "https://storage.googleapis.com/cuo-website/Observatory%20deck%20social.jpg",
  obsTour3: "https://storage.googleapis.com/cuo-website/ESB%20Observatory%20Tour%203.jpg",
  sam: "https://storage.googleapis.com/cuo-website/Sam%20Morril%20ESB.jpg",
  chris: "https://storage.googleapis.com/cuo-website/Chris%20ESB.jpg",
  zarna: "https://storage.googleapis.com/cuo-website/Zarna%20at%20ESB%20.jpg",
  emma: "https://storage.googleapis.com/cuo-website/Emma%20Willmann%20at%20ESB.jpg",
  matt: "https://storage.googleapis.com/cuo-website/Matt%20Richards%20at%20ESB.jpg",
  lenny: "https://storage.googleapis.com/cuo-website/Lenny%20Marcus.jpg",
  katz: "https://storage.googleapis.com/cuo-website/Katz%27s+Deli.jpg",
  katzEsb: "https://storage.googleapis.com/cuo-website/Katz's%20ESB.jpg",
  stTheos: "https://storage.googleapis.com/cuo-website/St.%20Theos.jpeg",
  distefanoEsb: "https://storage.googleapis.com/cuo-website/Distefano%20ESB.jpg",
  tourEsb: "https://storage.googleapis.com/cuo-website/UO%20at%20ESB%2091%20May%203%202026.jpeg",
  obsDeck: "https://storage.googleapis.com/cuo-website/ESB%20Obs%20Deck.jpg",
  amexLogo: "https://storage.googleapis.com/cuo-website/AMEX%20NEW%20LOGO.png",
  comedyuoLogo: "https://storage.googleapis.com/cuo-website/comedyuo-logo-white.png",
} as const;

const NAV = [
  { id: "residency", label: "The residency" },
  { id: "evening", label: "The evening" },
  { id: "first-show", label: "The first show" },
  { id: "amex-difference", label: "The Amex difference" },
  { id: "partner-model", label: "Partner model" },
  { id: "pricing", label: "Pricing" },
  { id: "whats-next", label: "What's next" },
] as const;

const FACTS = [
  { label: "Series", value: "4 February shows · 6:45–10 PM" },
  { label: "Planned audience", value: "1,100 guests across the series" },
  { label: "Programming", value: "20 comics across four nights" },
  { label: "Show dates", value: "February 4, 11, 18 & 25 · 2027" },
] as const;

const DATES = [
  { day: "04", show: "Show 1" },
  { day: "11", show: "Show 2" },
  { day: "18", show: "Show 3" },
  { day: "25", show: "Show 4" },
] as const;

const EVENING = [
  {
    time: "6:45 PM",
    title: "Arrival, food, and cocktails",
    body: "Light bites and drinks as guests mingle and find their seats.",
    images: [
      {
        src: IMG.katzEsb,
        alt: "Katz's hospitality at the Empire State Building show",
      },
      {
        src: IMG.food,
        alt: "Food and drinks during arrival at the Empire State Building",
      },
    ],
  },
  {
    time: "7:30 PM",
    title: "The show",
    body: "Four comics and one emcee perform; the bar remains open.",
    images: [
      {
        src: IMG.distefanoEsb,
        alt: "Chris Distefano performing at the Empire State Building",
      },
      {
        src: IMG.sam,
        alt: "Sam Morril performing at the Empire State Building",
      },
    ],
  },
  {
    time: "8:45 PM",
    title: "After the laughs",
    body: "Cocktails and conversation resume; the observatory tour begins.",
    images: [
      {
        src: IMG.tourEsb,
        alt: "ComedyUO tour group at the Empire State Building",
      },
      {
        src: IMG.tourGuide,
        alt: "Tour guide on the Empire State Building observatory",
      },
    ],
  },
  {
    time: "10:00 PM",
    title: "The night concludes",
    body: "The observatory tour closes and guests depart.",
    images: [
      {
        src: IMG.obsDeck,
        alt: "Empire State Building observatory deck",
      },
      {
        src: IMG.skyline,
        alt: "Guests looking out at the New York skyline",
      },
    ],
  },
] as const;

const DIFFERENCE_ROWS = [
  {
    moment: "The anticipation",
    residency: "Gets a confirmation email that they made the list.",
    amex:
      "Receives a customized ticket sent before their show, tailored to them, and also receives the complimentary tickets given out at the show.",
  },
  {
    moment: "Platinum access",
    residency:
      "The avg. member of our waitlist competes with 39,000 other members to get a ticket.",
    amex: "Protected ticket allocation for Amex Card Members, ~20 tickets per show.",
  },
  {
    moment: "VIP experience",
    residency: "Gets to sit within 5 rows of some of comedy's biggest names.",
    amex:
      "Select Amex members get to meet the talent backstage and in the green room before they perform.",
  },
  {
    moment: "The cherry on top",
    residency: "Gets a behind-the-scenes tour up to the observatory deck.",
    amex:
      "Gets a more exclusive, all-access tour of the building beyond just the observatory deck.",
  },
  {
    moment: "Walking out",
    residency: "Leaves with a full belly of Katz's.",
    amex: "Gets a complimentary ticket to another ComedyUO show of their choice.",
  },
] as const;

const CASE_CHAPTERS = [
  {
    title: "The lineup",
    figure: "5",
    figureLabel: "comedians",
    copy: "Sam Morril, Chris Distefano, Zarna Garg, Emma Willmann, Matt Richards, and Lenny Marcus opened a room that had never hosted comedy before. The night landed hard enough that we are coming back for a residency.",
    imageSrc: IMG.sam,
    imageAlt: "Sam Morril on stage at the Empire State Building",
  },
  {
    title: "The room & attendance",
    figure: "225",
    figureLabel: "guests",
    copy: "An intimate comedy room inside the landmark, packed end to end. Tickets, seats, and a crowd that felt like a private show in a public icon.",
    imageSrc: IMG.crowd,
    imageAlt: "Crowd at the Empire State Building comedy show",
  },
  {
    title: "Food & hospitality",
    figure: "Katz's Deli",
    figureLabel: "on the menu",
    copy: "World-famous food for a world-famous landmark. We called up Katz's to cater the night and bring two New York icons together.",
    imageSrc: "https://storage.googleapis.com/cuo-website/Katz's%20Bite.jpg",
    imageAlt: "Katz's food at the Empire State Building comedy show",
  },
] as const;

const MARCH_LINEUP = [
  {
    name: "Sam Morril",
    note: "March ESB",
    src: IMG.sam,
    instagram: "https://www.instagram.com/sammorril/",
    objectPosition: "center 20%",
  },
  {
    name: "Chris Distefano",
    note: "March ESB",
    src: IMG.chris,
    instagram: "https://www.instagram.com/chrisdcomedy/?hl=en",
    objectPosition: "center 18%",
  },
  {
    name: "Zarna Garg",
    note: "March ESB",
    src: IMG.zarna,
    instagram: "https://www.instagram.com/zarnagarg/",
    objectPosition: "center 22%",
  },
  {
    name: "Emma Willmann",
    note: "March ESB",
    src: IMG.emma,
    instagram: "https://www.instagram.com/emmawillmann/",
    objectPosition: "center 18%",
  },
  {
    name: "Matt Richards",
    note: "March ESB",
    src: IMG.matt,
    instagram: "https://www.instagram.com/mattwasfunny/",
    objectPosition: "center 20%",
  },
  {
    name: "Lenny Marcus",
    note: "March ESB",
    src: IMG.lenny,
    instagram: "https://www.instagram.com/lennymarcusnyc/",
    objectPosition: "center 15%",
  },
] as const;

const PRICING_PACKAGES = [
  { id: "pilot", name: "One-Night Pilot", investment: "$125,000" },
  { id: "series", name: "Two-Night Series", investment: "$240,000" },
  { id: "residency", name: "Full Residency", investment: "$400,000" },
] as const;

const PRICING_ROWS = [
  {
    label: "Shows",
    values: ["One", "Two", "Four"],
  },
  {
    label: "Guest access",
    values: ["20 guests", "40 admissions · 20 per show", "80 admissions · 20 per show"],
  },
  {
    label: "Mutually agreed headlining comedian",
    values: ["Included", "Included", "Included"],
  },
  {
    label: "VIP all-access ESB tour",
    values: ["Included", "Included", "Included"],
  },
  {
    label: "First access to the venue",
    values: ["Included", "Included", "Included"],
  },
  {
    label: "Custom tickets + take-home gift package",
    values: ["Included", "Included", "Included"],
  },
  {
    label: "Signed memorabilia for select guests",
    values: ["Included", "Included", "Included"],
  },
  {
    label: "VIP talent meet-up",
    values: ["Included", "Included at each show", "Included at each show"],
  },
  {
    label: "Headliner promotion in the Amex app",
    values: ["Included", "Included for both shows", "Included for all four shows"],
  },
] as const;

const STADIUM_STOPS = [
  {
    city: "Chicago",
    team: "Cubs",
    stadium: "Wrigley Field",
    image: "/images/partners/amex-esb/stadiums/wrigley.jpg",
  },
  {
    city: "Kansas City",
    team: "Chiefs",
    stadium: "Arrowhead Stadium",
    image: "/images/partners/amex-esb/stadiums/arrowhead.jpg",
  },
  {
    city: "San Diego",
    team: "Padres",
    stadium: "Petco Park",
    image: "/images/partners/amex-esb/stadiums/petco.jpg",
  },
  {
    city: "Chicago",
    team: "Bears",
    stadium: "Soldier Field",
    image: "/images/partners/amex-esb/stadiums/soldier.jpg",
  },
  {
    city: "Philadelphia",
    team: "Phillies",
    stadium: "Citizens Bank Park",
    image: "/images/partners/amex-esb/stadiums/citizens.jpg",
  },
  {
    city: "New York",
    team: "Mets",
    stadium: "Citi Field",
    image: "/images/partners/amex-esb/stadiums/citi.jpg",
  },
] as const;

const STADIUM_ROOMS = [
  "Locker rooms",
  "Batting cages",
  "Bullpens",
  "Media rooms",
  "Warning track",
] as const;

const CARRIER_TICKETS = [
  "Sizable reserved ticket allocation for customers",
  "Dedicated seating signage at the event",
  "Cut the line access",
  "Option to distribute tickets and market the night through their app or interface",
  "Right to promote a headliner and use comedian name, image, and likeness in their app",
] as const;

const AMEX_RESERVED = [
  "Reserved tickets for American Express Card Members across the series",
  "Custom tickets and pre-show packages shaped for Card Members",
  "Exclusive seating-area cutout for the Amex allocation",
  "Option to distribute tickets and market the residency through their app or interface",
  "Potential to promote a headliner and use comedian name, image, and likeness",
] as const;

const AMEX_VIP = [
  "Green-room access for VIP guests",
  "Talent memorabilia and signage moments",
] as const;

/**
 * Custom American Express Empire State Residency partnership concept page.
 */
export default function AmexResidencyPage({ campaign }: { campaign: SponsorCampaign }) {
  const [tab, setTab] = useState<"overview" | "residency">("overview");

  function openTab(next: "overview" | "residency") {
    setTab(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <Box sx={{ ...portalShellSx(campaign), bgcolor: "#000" }}>
      <Header
        tab={tab}
        onTabChange={openTab}
        residencyCtaHref={tab === "residency" ? "#pricing" : undefined}
      />
      {tab === "overview" ? (
        <AmexBrandOverview onContinueToResidency={() => openTab("residency")} />
      ) : (
        <>
          <Hero />
          <ResidencySection />
          <EveningSection />
          <FirstShowSection />
          <AmexDifferenceSection />
          <PartnerModelSection />
          <PricingSection />
          <WhatsNextSection />
        </>
      )}
      <Footer contactEmail={campaign.closing.contact.email} />
    </Box>
  );
}

function Header({
  tab,
  onTabChange,
  residencyCtaHref,
}: {
  tab: "overview" | "residency";
  onTabChange: (tab: "overview" | "residency") => void;
  residencyCtaHref?: string;
}) {
  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 40,
        bgcolor: "rgba(0,0,0,0.72)",
        backdropFilter: "blur(14px)",
        borderBottom: `1px solid ${LINE}`,
      }}
    >
      <Box
        sx={{
          maxWidth: PORTAL_MAX_WIDTH,
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          py: { xs: 1.25, md: 1.5 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: { xs: 1.25, md: 1.75 } }}>
          <AmexLogo height={{ xs: 26, md: 34 }} />
          <Box
            aria-hidden
            sx={{
              width: "1px",
              height: { xs: 20, md: 28 },
              bgcolor: "rgba(255,255,255,0.35)",
              flexShrink: 0,
            }}
          />
          <Box
            component="img"
            src={IMG.comedyuoLogo}
            alt="ComedyUO"
            sx={{
              height: { xs: 40, md: 56 },
              width: "auto",
              display: "block",
              flexShrink: 0,
              objectFit: "contain",
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 0.4,
            borderRadius: "999px",
            border: `1px solid ${LINE}`,
            bgcolor: "rgba(255,255,255,0.04)",
            flexShrink: 1,
            minWidth: 0,
          }}
        >
          {(
            [
              { id: "overview", label: "ComedyUO" },
              { id: "residency", label: "The residency" },
            ] as const
          ).map((item) => {
            const active = tab === item.id;
            return (
              <Button
                key={item.id}
                onClick={() => onTabChange(item.id)}
                sx={{
                  fontFamily: portalFont,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: { xs: "0.75rem", md: "0.8125rem" },
                  color: active ? "#0a0a0a" : MUTED,
                  bgcolor: active ? "#fff" : "transparent",
                  borderRadius: "999px",
                  px: { xs: 1.25, md: 2 },
                  py: 0.7,
                  minWidth: 0,
                  whiteSpace: "nowrap",
                  "&:hover": {
                    bgcolor: active ? "#fff" : "rgba(255,255,255,0.06)",
                    color: active ? "#0a0a0a" : "#fff",
                  },
                }}
              >
                {item.label}
              </Button>
            );
          })}
        </Box>

        {residencyCtaHref ? (
          <Button
            href={residencyCtaHref}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              fontFamily: portalFont,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              fontSize: "0.6875rem",
              color: "#fff",
              border: `1px solid rgba(255,255,255,0.45)`,
              borderRadius: "999px",
              px: 2,
              py: 0.85,
              flexShrink: 0,
              "&:hover": {
                borderColor: "#fff",
                bgcolor: "rgba(255,255,255,0.06)",
              },
            }}
          >
            Pricing
          </Button>
        ) : (
          <Box sx={{ display: { xs: "none", sm: "block" }, width: 88, flexShrink: 0 }} aria-hidden />
        )}
      </Box>

      {tab === "residency" && (
        <Box
          sx={{
            display: { xs: "none", xl: "block" },
            borderTop: `1px solid ${LINE}`,
          }}
        >
          <Box
            component="nav"
            sx={{
              maxWidth: PORTAL_MAX_WIDTH,
              mx: "auto",
              px: { xs: 2.5, md: 4 },
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 2,
              overflowX: "auto",
            }}
          >
            {NAV.map((item) => (
              <Link
                key={item.id}
                href={`#${item.id}`}
                underline="none"
                sx={{
                  fontFamily: portalFont,
                  fontSize: "0.625rem",
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: MUTED,
                  whiteSpace: "nowrap",
                  "&:hover": { color: "#fff" },
                }}
              >
                {item.label}
              </Link>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function Hero() {
  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        minHeight: { xs: "88vh", md: "92vh" },
        display: "flex",
        alignItems: "stretch",
        isolation: "isolate",
      }}
    >
      <Box
        component="img"
        src={IMG.tickets}
        alt="Empire State Building comedy show tickets held in the air"
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: { xs: "center center", md: "center 40%" },
          zIndex: 0,
          filter: "brightness(0.62) contrast(1.05)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          background: {
            xs: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.72) 45%, rgba(0,0,0,0.94) 100%)",
            md: "linear-gradient(90deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.72) 42%, rgba(0,0,0,0.28) 72%, rgba(0,0,0,0.55) 100%), linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.85) 100%)",
          },
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          maxWidth: PORTAL_MAX_WIDTH,
          width: "100%",
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          pt: { xs: 8, md: 14 },
          pb: { xs: 5, md: 6 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          minHeight: { xs: "88vh", md: "92vh" },
        }}
      >
        <Box sx={{ maxWidth: 640 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: portalFont,
              fontWeight: 700,
              color: "#fff",
              fontSize: "clamp(2.35rem, 6.5vw + 0.4rem, 4.75rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.035em",
              mb: { xs: 2, md: 2.5 },
            }}
          >
            The Empire State Residency.
          </Typography>

          <Typography
            sx={{
              fontFamily: portalFont,
              color: "rgba(244,245,247,0.82)",
              fontSize: { xs: "1rem", md: "1.125rem" },
              lineHeight: 1.65,
              maxWidth: 520,
              mb: { xs: 3, md: 3.5 },
            }}
          >
            The first-ever comedy residency at the Empire State Building. Four February shows,
            twenty comics, and a distinct Card Member experience imagined with American Express.
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
            <PillButton href="#residency" solid>
              See the story
            </PillButton>
            <PillButton href="#amex-difference">The Amex difference</PillButton>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 2,
            mt: 6,
          }}
        >
          <Typography
            sx={{
              fontFamily: portalFont,
              fontSize: "0.625rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.45)",
              maxWidth: 360,
            }}
          >
            Photographs from the first Empire State Building comedy show · March
          </Typography>
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              color: "rgba(255,255,255,0.55)",
            }}
          >
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.625rem",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              Scroll
            </Typography>
            <Box sx={{ width: "1px", height: 48, bgcolor: "rgba(255,255,255,0.45)" }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function ResidencySection() {
  return (
    <Section id="residency">
      <Eyebrow>The residency</Eyebrow>
      <Title>Four nights. One unforgettable address.</Title>
      <Body sx={{ maxWidth: 720, mb: { xs: 4, md: 5 } }}>
        We transform iconic spaces into unique, after-hours comedy venues. Now we are
        heading to the most iconic building in the world for a winter residency: stand-up, open-bar
        hospitality, and an observatory tour, four nights in February 2027.
      </Body>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
          borderTop: `1px solid ${LINE}`,
          borderBottom: `1px solid ${LINE}`,
          mb: { xs: 7, md: 10 },
        }}
      >
        {FACTS.map((fact, i) => (
          <Box
            key={fact.label}
            sx={{
              px: { xs: 0, md: 3 },
              py: { xs: 2.5, md: 3.5 },
              borderRight: {
                xs: "none",
                sm: i % 2 === 0 ? `1px solid ${LINE}` : "none",
                md: i < FACTS.length - 1 ? `1px solid ${LINE}` : "none",
              },
              borderBottom: {
                xs: i < FACTS.length - 1 ? `1px solid ${LINE}` : "none",
                sm: i < 2 ? `1px solid ${LINE}` : "none",
                md: "none",
              },
              pl: { md: i === 0 ? 0 : 3 },
              pr: { md: i === FACTS.length - 1 ? 0 : 3 },
            }}
          >
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: MUTED,
                mb: 1.25,
              }}
            >
              {fact.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 600,
                color: "#fff",
                fontSize: { xs: "1rem", md: "1.0625rem" },
                lineHeight: 1.4,
              }}
            >
              {fact.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Eyebrow accent>Four dates · February 2027</Eyebrow>
      <Title sx={{ mb: { xs: 3, md: 4 } }}>One residency, four nights.</Title>
      <Box sx={{ borderTop: `1px solid ${LINE}` }}>
        {DATES.map((d) => (
          <Box
            key={d.day}
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 2,
              py: { xs: 2.25, md: 2.75 },
              borderBottom: `1px solid ${LINE}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
              <Typography
                sx={{
                  fontFamily: portalFont,
                  fontWeight: 700,
                  color: "#fff",
                  fontSize: { xs: "2.25rem", md: "3rem" },
                  letterSpacing: "-0.03em",
                  lineHeight: 1,
                }}
              >
                {d.day}
              </Typography>
              <Typography
                sx={{
                  fontFamily: portalFont,
                  color: MUTED,
                  fontSize: { xs: "1rem", md: "1.125rem" },
                }}
              >
                February
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: MUTED,
              }}
            >
              {d.show}
            </Typography>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function EveningSection() {
  return (
    <Section id="evening" sx={{ pt: { xs: 2, md: 3 }, pb: { xs: 5, md: 6 } }}>
      <Eyebrow accent>The evening · 6:45–10 PM</Eyebrow>
      <Title>How each residency night runs.</Title>
      <Body sx={{ maxWidth: 680, mb: { xs: 3, md: 4 } }}>
        Same arc every night. Arrival, food, and cocktails, the show, post-show mingling, then up to
        the observatory.
      </Body>

      <Box
        sx={{
          position: "relative",
          maxWidth: 1100,
          py: { xs: 0.5, md: 1 },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: { xs: 11, md: 15 },
            top: 12,
            bottom: 12,
            width: 0,
            borderLeft: "2px dotted rgba(255,255,255,0.28)",
            zIndex: 0,
          }}
        />

        <Box sx={{ display: "grid", gap: { xs: 4, md: 5.5 }, position: "relative", zIndex: 1 }}>
          {EVENING.map((step) => (
            <TrailStopCard key={step.time} step={step} />
          ))}
        </Box>
      </Box>
    </Section>
  );
}

function TrailStopCard({
  step,
}: {
  step: (typeof EVENING)[number];
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "24px 1fr", md: "32px 1fr" },
        columnGap: { xs: 1.5, md: 2.5 },
        alignItems: "start",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          pt: { xs: 0.35, md: 0.55 },
          position: "relative",
          zIndex: 2,
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: { xs: 14, md: 16 },
            height: { xs: 14, md: 16 },
            borderRadius: "50%",
            bgcolor: "#006FCF",
            border: "3px solid #000",
            boxShadow: "0 0 0 3px rgba(0,111,207,0.45)",
            flexShrink: 0,
          }}
        />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            gap: { xs: 0.75, md: 1.25 },
            mb: { xs: 1.25, md: 1.5 },
          }}
        >
          <Typography
            sx={{
              fontFamily: portalFont,
              fontWeight: 700,
              color: ACCENT,
              fontSize: { xs: "1.35rem", md: "1.6rem" },
              letterSpacing: "-0.02em",
              lineHeight: 1,
            }}
          >
            {step.time}
          </Typography>
          <Typography
            sx={{
              fontFamily: portalFont,
              fontWeight: 700,
              color: "#fff",
              fontSize: { xs: "1.05rem", md: "1.25rem" },
              lineHeight: 1.15,
            }}
          >
            {step.title}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: { xs: 1, md: 1.25 },
            mb: { xs: 1.25, md: 1.5 },
          }}
        >
          {step.images.map((image) => (
            <Box
              key={image.src}
              sx={{
                borderRadius: { xs: "14px", md: "18px" },
                overflow: "hidden",
                border: `1px solid ${LINE}`,
                bgcolor: "#121212",
                aspectRatio: { xs: "16 / 10", sm: "4 / 3" },
                minHeight: { xs: 200, sm: 240, md: 300 },
              }}
            >
              <Box
                component="img"
                src={image.src}
                alt={image.alt}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Box>
          ))}
        </Box>

        <Typography
          sx={{
            fontFamily: portalFont,
            color: MUTED,
            fontSize: { xs: "0.9375rem", md: "1rem" },
            lineHeight: 1.5,
            maxWidth: 720,
          }}
        >
          {step.body}
        </Typography>
      </Box>
    </Box>
  );
}

function AmexDifferenceSection() {
  return (
    <Section id="amex-difference" sx={{ pt: { xs: 4, md: 6 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
        <AmexLogo height={{ xs: 22, md: 26 }} />
        <Eyebrow sx={{ mb: 0 }}>The American Express opportunity</Eyebrow>
      </Box>
      <Title>The city’s newest residency. The Amex inside track.</Title>
      <Body sx={{ maxWidth: 760, mb: { xs: 4, md: 5 } }}>
        The residency already has a compelling arc. An Amex partnership could add a distinct,
        recognizable Card Member layer at each moment. This is how the experience can differ from a
        typical waitlist guest.
      </Body>

      <Box sx={{ borderTop: `1px solid ${LINE}`, display: { xs: "none", md: "block" } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "0.7fr 1.15fr 1.35fr",
            gap: 3,
            py: 2,
            borderBottom: `1px solid ${LINE}`,
          }}
        >
          {["Moment", "The avg. attendee", "Amex Card Members · Proposed"].map((h, i) => (
            <Typography
              key={h}
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: i === 2 ? ACCENT : MUTED,
              }}
            >
              {h}
            </Typography>
          ))}
        </Box>
        {DIFFERENCE_ROWS.map((row) => (
          <Box
            key={row.moment}
            sx={{
              display: "grid",
              gridTemplateColumns: "0.7fr 1.15fr 1.35fr",
              gap: 3,
              py: 3,
              borderBottom: `1px solid ${LINE}`,
              alignItems: "start",
            }}
          >
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "#fff",
                fontSize: "1.125rem",
              }}
            >
              {row.moment}
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: MUTED, lineHeight: 1.55 }}>
              {row.residency}
            </Typography>
            <Box sx={{ borderLeft: `2px solid ${ACCENT}`, pl: 2.5 }}>
              <Typography sx={{ fontFamily: portalFont, color: "#fff", lineHeight: 1.55 }}>
                {row.amex}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: { xs: "grid", md: "none" }, gap: 2.5 }}>
        {DIFFERENCE_ROWS.map((row) => (
          <Box key={row.moment} sx={{ borderTop: `1px solid ${LINE}`, pt: 2.5 }}>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "#fff",
                fontSize: "1.125rem",
                mb: 1.5,
              }}
            >
              {row.moment}
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: MUTED,
                mb: 0.75,
              }}
            >
              The avg. attendee
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: MUTED, mb: 1.75, lineHeight: 1.55 }}>
              {row.residency}
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: ACCENT,
                mb: 0.75,
              }}
            >
              Amex Card Members · Proposed
            </Typography>
            <Box sx={{ borderLeft: `2px solid ${ACCENT}`, pl: 2 }}>
              <Typography sx={{ fontFamily: portalFont, color: "#fff", lineHeight: 1.55 }}>
                {row.amex}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function FirstShowSection() {
  return (
    <Section id="first-show">
      <Eyebrow>Case study · March</Eyebrow>
      <Title>The first Empire State Building show</Title>
      <Body sx={{ maxWidth: 760, mb: { xs: 4, md: 5 } }}>
        In March we threw the first comedy show the Empire State Building has ever seen, and it was
        incredible. Here is how the night went: the lineup, the room, the hospitality, and the
        observatory finish. Press from Time Out NY and CBS NYC followed.
      </Body>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
          gap: { xs: 2, md: 2.5 },
          mb: { xs: 5, md: 7 },
        }}
      >
        {CASE_CHAPTERS.map((card) => (
          <CaseFigureCard key={card.title} card={card} />
        ))}
      </Box>

      <Panel>
        <Eyebrow>After the set</Eyebrow>
        <Typography
          sx={{
            fontFamily: portalFont,
            fontWeight: 700,
            color: "#fff",
            fontSize: { xs: "1.35rem", md: "1.65rem" },
            letterSpacing: "-0.02em",
            mb: 1.25,
          }}
        >
          Observatory tour
        </Typography>
        <Body sx={{ mb: 2.5, maxWidth: 720 }}>
          Guests moved from the room up to the deck: the Empire State Building arch, skyline
          views, and a night that ended above the city.
        </Body>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "1.15fr 1fr 1fr" },
            gap: { xs: 1.5, md: 1.75 },
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              borderRadius: "12px",
              overflow: "hidden",
              border: `1px solid ${LINE}`,
              bgcolor: "#121212",
              aspectRatio: "1 / 1",
              gridColumn: { xs: "1 / -1", md: "auto" },
            }}
          >
            <Box
              component="img"
              src={IMG.observatoryCouple}
              alt="Guests posing under the Empire State Building observatory arch"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center 40%",
                display: "block",
              }}
            />
          </Box>
          <MiniPhoto src={IMG.esbTopDeck} alt="Empire State Building observatory top deck" />
          <MiniPhoto
            src={IMG.obsTour3}
            alt="Guests on the Empire State Building observatory tour"
          />
        </Box>
      </Panel>

      <Box sx={{ mt: { xs: 6, md: 8 } }}>
        <Eyebrow accent>March lineup</Eyebrow>
        <Title sx={{ mb: 1.5 }}>The comics who opened the building.</Title>
        <Body sx={{ maxWidth: 680, mb: { xs: 3, md: 4 } }}>
          Comics from the first ESB show. Tap a card for Instagram.
        </Body>
        <MarchLineupGrid />
      </Box>
    </Section>
  );
}

function PartnerModelSection() {
  return (
    <Section id="partner-model">
      <Eyebrow>How we work with partners</Eyebrow>
      <Title>A Card Member layer, proven at Katz&apos;s with a wireless carrier partner.</Title>
      <Body sx={{ maxWidth: 760, mb: { xs: 4, md: 5 } }}>
        Before pricing, here is how partner access works today. At Katz&apos;s, our wireless carrier
        partner gets a sizable ticket allocation for customers. For the residency, American Express
        can go further: reserved Card Member tickets, custom packages, and a VIP layer with
        green-room access and talent memorabilia.
      </Body>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: { xs: 2, md: 2.5 },
          mb: { xs: 3, md: 4 },
        }}
      >
        <Panel>
          <Box
            component="img"
            src={IMG.katz}
            alt="Katz's Delicatessen, ComedyUO venue"
            sx={{
              width: "100%",
              height: { xs: 180, md: 220 },
              objectFit: "cover",
              borderRadius: "10px",
              mb: 2.25,
              display: "block",
            }}
          />
          <Typography
            sx={{
              fontFamily: portalFont,
              fontSize: "0.6875rem",
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: ACCENT,
              mb: 1,
            }}
          >
            Example · Katz&apos;s × Wireless Carrier Partner · One evening
          </Typography>
          <Typography
            sx={{
              fontFamily: portalFont,
              fontWeight: 700,
              color: "#fff",
              fontSize: { xs: "1.25rem", md: "1.5rem" },
              lineHeight: 1.2,
              mb: 2,
            }}
          >
            Sizable ticket allocation for customers
          </Typography>
          <Typography
            sx={{
              fontFamily: portalFont,
              fontSize: "0.6875rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: MUTED,
              mb: 1.25,
            }}
          >
            Reserved tickets · Sizable allocation
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none" }}>
            {CARRIER_TICKETS.map((item) => (
              <CheckRow key={item}>{item}</CheckRow>
            ))}
          </Box>
        </Panel>

        <Panel>
          <Box
            component="img"
            src={IMG.socializing}
            alt="Guests socializing at the Empire State Building show"
            sx={{
              width: "100%",
              height: { xs: 180, md: 220 },
              objectFit: "cover",
              borderRadius: "10px",
              mb: 2.25,
              display: "block",
            }}
          />
          <Typography
            sx={{
              fontFamily: portalFont,
              fontSize: "0.6875rem",
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: ACCENT,
              mb: 1,
            }}
          >
            Proposed · Empire State Residency × Amex · Four nights
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <AmexLogo height={{ xs: 24, md: 28 }} />
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "#fff",
                fontSize: { xs: "1.25rem", md: "1.5rem" },
                lineHeight: 1.2,
              }}
            >
              Similar model, with a VIP layer
            </Typography>
          </Box>
          <Body sx={{ mb: 2.5 }}>
            American Express gets reserved Card Member tickets across the residency, plus custom
            packages and an exclusive seating cutout. VIP guests can add green-room access and
            talent memorabilia moments.
          </Body>
          <Typography
            sx={{
              fontFamily: portalFont,
              fontSize: "0.6875rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: MUTED,
              mb: 1.25,
            }}
          >
            Reserved Card Member access
          </Typography>
          <Box component="ul" sx={{ m: 0, mb: 2.5, pl: 0, listStyle: "none" }}>
            {AMEX_RESERVED.map((item) => (
              <CheckRow key={item}>{item}</CheckRow>
            ))}
          </Box>
          <Typography
            sx={{
              fontFamily: portalFont,
              fontSize: "0.6875rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: MUTED,
              mb: 1.25,
            }}
          >
            VIP layer
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none" }}>
            {AMEX_VIP.map((item) => (
              <CheckRow key={item}>{item}</CheckRow>
            ))}
          </Box>
        </Panel>
      </Box>

      <Panel>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "0.9fr 1.3fr" },
            gap: { xs: 2.25, md: 3 },
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src={IMG.stTheos}
            alt="Neighborhood comedy night at St. Theo's for BILT members"
            sx={{
              width: "100%",
              height: { xs: 200, md: 240 },
              objectFit: "cover",
              borderRadius: "10px",
              display: "block",
            }}
          />
          <Box>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: ACCENT,
                mb: 1,
              }}
            >
              Example · Neighborhood Comedy × BILT
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Typography
                sx={{
                  fontFamily: portalFont,
                  fontWeight: 700,
                  color: "#fff",
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                  lineHeight: 1.2,
                }}
              >
                Comedy that won&apos;t ruffle feathers.
              </Typography>
              <Box
                component="img"
                src="/images/private-events-BILT_Rewards_Logo.png"
                alt="BILT"
                sx={{
                  height: { xs: 22, md: 26 },
                  width: "auto",
                  maxWidth: 120,
                  objectFit: "contain",
                  display: "block",
                  flexShrink: 0,
                  filter: "brightness(0) invert(1)",
                }}
              />
            </Box>
            <Body sx={{ mb: 2 }}>
              We produce Neighborhood Comedy nights for BILT members at spots like St. Theo&apos;s:
              curated rooms, vetted comics, and a ticket release that goes straight to their
              membership.
            </Body>
            <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none" }}>
              <CheckRow>
                Comics selected to fit BILT&apos;s demographics and brand tone
              </CheckRow>
              <CheckRow>
                Talent vetted so the night stays sharp without ruffling feathers
              </CheckRow>
              <CheckRow>BILT releases tickets directly to members</CheckRow>
              <CheckRow>Intimate neighborhood venues, produced end to end by ComedyUO</CheckRow>
            </Box>
          </Box>
        </Box>
      </Panel>
    </Section>
  );
}

function PricingSection() {
  return (
    <Section id="pricing">
      <Eyebrow>Partnership options · Proposed</Eyebrow>
      <Title>A clear place in the residency.</Title>
      <Body sx={{ maxWidth: 720, mb: { xs: 4, md: 5 } }}>
        Three partnership levels built around Card Member access, hospitality, and a mutually
        agreed headliner. Full-residency pricing reflects an upfront four-show commitment.
      </Body>

      {/* Desktop comparison table */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          borderTop: `1px solid ${LINE}`,
          overflowX: "auto",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1.35fr repeat(3, minmax(140px, 1fr))",
            gap: 0,
            minWidth: 780,
          }}
        >
          <Box sx={{ py: 2.25, pr: 2, borderBottom: `1px solid ${LINE}` }} />
          {PRICING_PACKAGES.map((pkg) => (
            <Box
              key={pkg.id}
              sx={{
                py: 2.25,
                px: 2,
                borderBottom: `1px solid ${LINE}`,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontFamily: portalFont,
                  fontWeight: 700,
                  color: "#fff",
                  fontSize: { md: "1rem", lg: "1.0625rem" },
                  lineHeight: 1.25,
                  mb: 0.75,
                }}
              >
                {pkg.name}
              </Typography>
              <Typography
                sx={{
                  fontFamily: portalFont,
                  fontWeight: 700,
                  color: ACCENT,
                  fontSize: { md: "1.35rem", lg: "1.5rem" },
                  letterSpacing: "-0.02em",
                }}
              >
                {pkg.investment}
              </Typography>
            </Box>
          ))}

          {PRICING_ROWS.map((row) => (
            <Box key={row.label} sx={{ display: "contents" }}>
              <Box
                sx={{
                  py: 2.1,
                  pr: 2,
                  borderBottom: `1px solid ${LINE}`,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: portalFont,
                    color: MUTED,
                    fontSize: "0.9375rem",
                    lineHeight: 1.4,
                  }}
                >
                  {row.label}
                </Typography>
              </Box>
              {row.values.map((value, i) => (
                <Box
                  key={`${row.label}-${PRICING_PACKAGES[i].id}`}
                  sx={{
                    py: 2.1,
                    px: 2,
                    borderBottom: `1px solid ${LINE}`,
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: portalFont,
                      fontWeight: 500,
                      color: "#fff",
                      fontSize: "0.9375rem",
                      lineHeight: 1.4,
                    }}
                  >
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Mobile stacked cards */}
      <Box sx={{ display: { xs: "grid", md: "none" }, gap: 2.5 }}>
        {PRICING_PACKAGES.map((pkg, pkgIndex) => (
          <Box
            key={pkg.id}
            sx={{
              borderRadius: RADIUS,
              border: `1px solid ${LINE}`,
              bgcolor: CARD_BG,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: 2.25,
                py: 2,
                borderBottom: `1px solid ${LINE}`,
              }}
            >
              <Typography
                sx={{
                  fontFamily: portalFont,
                  fontWeight: 700,
                  color: "#fff",
                  fontSize: "1.125rem",
                  mb: 0.5,
                }}
              >
                {pkg.name}
              </Typography>
              <Typography
                sx={{
                  fontFamily: portalFont,
                  fontWeight: 700,
                  color: ACCENT,
                  fontSize: "1.75rem",
                  letterSpacing: "-0.03em",
                }}
              >
                {pkg.investment}
              </Typography>
            </Box>
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
              {PRICING_ROWS.map((row) => (
                <Box
                  key={row.label}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1.5,
                    px: 2.25,
                    py: 1.5,
                    borderBottom: `1px solid ${LINE}`,
                    "&:last-of-type": { borderBottom: "none" },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: portalFont,
                      color: MUTED,
                      fontSize: "0.8125rem",
                      lineHeight: 1.4,
                    }}
                  >
                    {row.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: portalFont,
                      color: "#fff",
                      fontSize: "0.875rem",
                      lineHeight: 1.4,
                      textAlign: "right",
                    }}
                  >
                    {row.values[pkgIndex]}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>

      <Typography
        sx={{
          fontFamily: portalFont,
          fontSize: "0.75rem",
          fontStyle: "italic",
          color: "rgba(255,255,255,0.4)",
          lineHeight: 1.55,
          mt: 3,
          maxWidth: 900,
        }}
      >
        Talent selection, promotional assets, usage period, and talent participation mutually
        agreed and secured in the talent agreement. Full-residency pricing reflects an upfront
        four-show commitment.
      </Typography>
    </Section>
  );
}

function WhatsNextSection() {
  return (
    <Box
      id="whats-next"
      component="section"
      sx={{
        position: "relative",
        isolation: "isolate",
        py: { xs: 8, md: 12 },
        scrollMarginTop: 88,
      }}
    >
      <Box
        component="img"
        src={IMG.skyline}
        alt=""
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 0,
          filter: "brightness(0.42)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.62) 45%, rgba(0,0,0,0.88) 100%)",
        }}
      />
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          maxWidth: PORTAL_MAX_WIDTH,
          mx: "auto",
          px: { xs: 2.5, md: 4 },
        }}
      >
        <Eyebrow>What&apos;s next · Stadium tour</Eyebrow>
        <Title sx={{ mb: 2, maxWidth: 780 }}>
          Start with the residency. Then go national with us.
        </Title>
        <Body sx={{ maxWidth: 760, mb: { xs: 4, md: 5 }, color: "rgba(244,245,247,0.78)" }}>
          After Empire State, the next chapter is a proposed stadium comedy tour: intimate live
          comedy inside spaces most fans never see, built as partnerships with the stadiums
          themselves. Same ComedyUO brand, national footprint. We are open to exploring an American
          Express role there too.
        </Body>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.15fr 0.85fr" },
            gap: 2,
            mb: { xs: 3, md: 4 },
          }}
        >
          <Box
            sx={{
              p: { xs: 2.5, md: 3.5 },
              borderRadius: RADIUS,
              border: `1px solid ${LINE}`,
              bgcolor: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(10px)",
            }}
          >
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: ACCENT,
                mb: 1.25,
              }}
            >
              The idea
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "#fff",
                fontSize: { xs: "1.35rem", md: "1.65rem" },
                letterSpacing: "-0.02em",
                mb: 1.5,
                lineHeight: 1.2,
              }}
            >
              A big-stage setting. An intimate-show feeling.
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: MUTED, lineHeight: 1.65, mb: 2.5 }}>
              Activating America&apos;s most iconic stadiums in ways they have never seen before:
              close-up stand-up in surprising rooms inside the venue, with premium hospitality
              around the set. Guests get access to spaces usually reserved for players and staff.
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: MUTED,
                mb: 1.25,
              }}
            >
              Rooms we&apos;re tapping
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {STADIUM_ROOMS.map((room) => (
                <Box
                  key={room}
                  sx={{
                    px: 1.25,
                    py: 0.65,
                    borderRadius: "999px",
                    border: `1px solid ${LINE}`,
                    fontFamily: portalFont,
                    fontSize: "0.8125rem",
                    color: "#fff",
                  }}
                >
                  {room}
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              p: { xs: 2.5, md: 3.5 },
              borderRadius: RADIUS,
              border: `1px solid ${LINE}`,
              bgcolor: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(10px)",
            }}
          >
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: ACCENT,
                mb: 1.25,
              }}
            >
              The partnerships
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "#fff",
                fontSize: { xs: "1.35rem", md: "1.65rem" },
                letterSpacing: "-0.02em",
                mb: 1.5,
              }}
            >
              Built with the stadiums
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: MUTED, lineHeight: 1.65, mb: 2 }}>
              These are partnerships with the stadiums, not a rented-room tour. We work directly
              with each venue to open spaces fans never get into for live comedy and hospitality.
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: "#fff", lineHeight: 1.55 }}>
              Arrive, connect, laugh, stay. Comics rotate room to room while the bar stays open.
            </Typography>
          </Box>
        </Box>

        <Typography
          sx={{
            fontFamily: portalFont,
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: MUTED,
            mb: 1.75,
          }}
        >
          Stadium partnerships in discussion
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(3, 1fr)",
            },
            gap: 1.5,
            mb: 3,
          }}
        >
          {STADIUM_STOPS.map((stop) => (
            <Box
              key={`${stop.city}-${stop.team}`}
              sx={{
                position: "relative",
                borderRadius: RADIUS,
                border: `1px solid ${LINE}`,
                overflow: "hidden",
                aspectRatio: "16 / 10",
                bgcolor: "#121212",
              }}
            >
              <Box
                component="img"
                src={stop.image}
                alt={`${stop.stadium}, home of the ${stop.team}`}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                  display: "block",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  px: { xs: 1.5, md: 1.75 },
                  py: { xs: 1.25, md: 1.5 },
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.82) 45%, rgba(0,0,0,0.94) 100%)",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: portalFont,
                    fontSize: "0.625rem",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.7)",
                    mb: 0.4,
                  }}
                >
                  {stop.city}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: portalFont,
                    fontWeight: 700,
                    color: "#fff",
                    fontSize: { xs: "1.0625rem", md: "1.125rem" },
                    lineHeight: 1.15,
                    mb: 0.25,
                  }}
                >
                  {stop.team}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: portalFont,
                    fontSize: "0.8125rem",
                    color: "rgba(255,255,255,0.72)",
                  }}
                >
                  {stop.stadium}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Typography
          sx={{
            fontFamily: portalFont,
            fontSize: "0.75rem",
            color: "rgba(255,255,255,0.45)",
            lineHeight: 1.55,
            maxWidth: 820,
            mb: 3.5,
          }}
        >
          Footprint drawn from ComedyUO&apos;s stadium-tour concept work with stadium partners.
          All assets and activations are subject to stadium approval and existing category
          exclusivity. This section is a teaser for American Express, not a committed Amex stadium
          package.
        </Typography>

        <PillButton href="mailto:mike@comedyuo.com">Talk stadium tour</PillButton>
      </Box>
    </Box>
  );
}

function Footer({ contactEmail }: { contactEmail: string }) {
  return (
    <Box component="footer" sx={{ borderTop: `1px solid ${LINE}`, py: { xs: 4, md: 5 } }}>
      <Box
        sx={{
          maxWidth: PORTAL_MAX_WIDTH,
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <AmexLogo height={{ xs: 20, md: 24 }} />
          <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.875rem" }}>
            ComedyUO × American Express · Partnership concept
          </Typography>
        </Box>
        <Link
          href={`mailto:${contactEmail}`}
          underline="hover"
          sx={{ fontFamily: portalFont, color: "#fff", fontSize: "0.875rem" }}
        >
          {contactEmail}
        </Link>
      </Box>
    </Box>
  );
}

function MarchLineupGrid() {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr 1fr",
          md: "repeat(3, 1fr)",
        },
        gap: { xs: 1.5, md: 2 },
        alignItems: "stretch",
      }}
    >
      {MARCH_LINEUP.map((item) => (
        <Link
          key={item.name}
          href={item.instagram}
          target="_blank"
          rel="noopener noreferrer"
          underline="none"
          sx={{
            display: "flex",
            flexDirection: "column",
            color: "inherit",
            borderRadius: RADIUS,
            border: `1px solid ${LINE}`,
            bgcolor: CARD_BG,
            overflow: "hidden",
            height: "100%",
            transition: "border-color 0.15s ease",
            "&:hover": { borderColor: "rgba(255,255,255,0.35)" },
          }}
        >
          <Box
            sx={{
              aspectRatio: "3 / 4",
              bgcolor: "#121212",
              borderBottom: `1px solid ${LINE}`,
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={item.src}
              alt={item.name}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: item.objectPosition,
                display: "block",
              }}
            />
          </Box>
          <Box
            sx={{
              p: { xs: 1.5, md: 1.75 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              minHeight: { xs: 84, md: 92 },
              flex: 1,
            }}
          >
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "#fff",
                fontSize: { xs: "0.95rem", md: "1.0625rem" },
                mb: 0.4,
                lineHeight: 1.25,
              }}
            >
              {item.name}
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.75rem",
                color: MUTED,
                mb: 0.75,
              }}
            >
              {item.note}
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: ACCENT,
              }}
            >
              Instagram
            </Typography>
          </Box>
        </Link>
      ))}
    </Box>
  );
}

function Section({
  id,
  children,
  sx = {},
}: {
  id: string;
  children: React.ReactNode;
  sx?: Record<string, unknown>;
}) {
  return (
    <Box
      id={id}
      component="section"
      sx={{
        maxWidth: PORTAL_MAX_WIDTH,
        mx: "auto",
        px: { xs: 2.5, md: 4 },
        py: { xs: 8, md: 12 },
        scrollMarginTop: 88,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        p: { xs: 2.5, md: 3.5 },
        borderRadius: RADIUS,
        border: `1px solid ${LINE}`,
        bgcolor: CARD_BG,
        height: "100%",
      }}
    >
      {children}
    </Box>
  );
}

function CheckRow({ children }: { children: React.ReactNode }) {
  return (
    <Box component="li" sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", mb: 1.1 }}>
      <CheckIcon sx={{ fontSize: 18, color: "#fff", mt: "2px", flexShrink: 0 }} />
      <Typography sx={{ fontFamily: portalFont, color: "#fff", lineHeight: 1.5, fontSize: "0.95rem" }}>
        {children}
      </Typography>
    </Box>
  );
}

function MiniPhoto({ src, alt }: { src: string; alt: string }) {
  return (
    <Box
      sx={{
        borderRadius: "8px",
        overflow: "hidden",
        aspectRatio: "1 / 1",
        border: `1px solid ${LINE}`,
        bgcolor: "#121212",
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
    </Box>
  );
}

function CaseFigureCard({
  card,
}: {
  card: (typeof CASE_CHAPTERS)[number];
}) {
  const [open, setOpen] = useState(false);
  const longFigure = card.figure.length > 4;

  return (
    <Box
      sx={{
        borderRadius: RADIUS,
        border: `1px solid ${LINE}`,
        bgcolor: CARD_BG,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box
        component="img"
        src={card.imageSrc}
        alt={card.imageAlt}
        sx={{
          width: "100%",
          height: { xs: 160, md: 180 },
          objectFit: "cover",
          display: "block",
          flexShrink: 0,
        }}
      />
      <Box sx={{ p: { xs: 2, md: 2.5 }, display: "flex", flexDirection: "column", flex: 1 }}>
        <Typography
          sx={{
            fontFamily: portalFont,
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: MUTED,
            mb: 1,
          }}
        >
          {card.title}
        </Typography>
        <Typography
          component="p"
          sx={{
            fontFamily: portalFont,
            fontWeight: 700,
            color: "#fff",
            fontSize: longFigure
              ? { xs: "1.75rem", md: "2.15rem" }
              : { xs: "3.25rem", md: "4rem" },
            letterSpacing: "-0.04em",
            lineHeight: 1,
            mb: 0.75,
          }}
        >
          {card.figure}
        </Typography>
        <Typography
          sx={{
            fontFamily: portalFont,
            fontSize: "0.875rem",
            color: ACCENT,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            fontWeight: 600,
            mb: 2,
          }}
        >
          {card.figureLabel}
        </Typography>

        <Box sx={{ mt: "auto" }}>
          <Button
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  fontSize: 20,
                  transform: open ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s ease",
                }}
              />
            }
            sx={{
              fontFamily: portalFont,
              fontWeight: 600,
              textTransform: "none",
              color: "#fff",
              px: 0,
              minWidth: 0,
              justifyContent: "flex-start",
              "&:hover": { bgcolor: "transparent", color: ACCENT },
            }}
          >
            {open ? "Hide details" : "Read more"}
          </Button>
          <Collapse in={open}>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.9375rem",
                lineHeight: 1.65,
                color: MUTED,
                pt: 1.25,
                pb: 0.5,
              }}
            >
              {card.copy}
            </Typography>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
}

function AmexLogo({
  height,
}: {
  height: { xs: number; md: number } | number;
}) {
  const h = typeof height === "number" ? { xs: height, md: height } : height;
  return (
    <Box
      component="img"
      src={IMG.amexLogo}
      alt="American Express"
      sx={{
        height: h,
        width: "auto",
        display: "block",
        flexShrink: 0,
        objectFit: "contain",
      }}
    />
  );
}

function Eyebrow({
  children,
  accent = false,
  sx = {},
}: {
  children: React.ReactNode;
  accent?: boolean;
  sx?: Record<string, unknown>;
}) {
  return (
    <Typography
      sx={{
        fontFamily: portalFont,
        fontSize: "0.6875rem",
        fontWeight: 600,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color: accent ? ACCENT : MUTED,
        mb: 1.5,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

function Title({
  children,
  sx = {},
}: {
  children: React.ReactNode;
  sx?: Record<string, unknown>;
}) {
  return (
    <Typography
      component="h2"
      sx={{
        fontFamily: portalFont,
        fontWeight: 700,
        color: "#fff",
        fontSize: "clamp(1.85rem, 2.8vw + 1rem, 3rem)",
        letterSpacing: "-0.03em",
        lineHeight: 1.1,
        mb: { xs: 2, md: 2.5 },
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

function Body({
  children,
  sx = {},
}: {
  children: React.ReactNode;
  sx?: Record<string, unknown>;
}) {
  return (
    <Typography
      sx={{
        fontFamily: portalFont,
        color: MUTED,
        fontSize: { xs: "0.975rem", md: "1.0625rem" },
        lineHeight: 1.65,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

function PillButton({
  href,
  children,
  solid = false,
}: {
  href: string;
  children: React.ReactNode;
  solid?: boolean;
}) {
  return (
    <Button
      href={href}
      endIcon={solid ? <SouthIcon sx={{ fontSize: 15 }} /> : undefined}
      sx={{
        fontFamily: portalFont,
        fontWeight: 700,
        textTransform: "none",
        borderRadius: "999px",
        px: 2.75,
        py: 1.2,
        fontSize: "0.9375rem",
        ...(solid
          ? {
              bgcolor: "#fff",
              color: "#0a0a0a",
              "&:hover": { bgcolor: "#fff", filter: "brightness(0.94)" },
            }
          : {
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.45)",
              "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.06)" },
            }),
      }}
    >
      {children}
    </Button>
  );
}